import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import { memo, useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { makeBasketData } from "../ExtraUtils";
import {
  StickyCheckoutNotificationProps,
  StickyIcons,
  StickyLabels,
} from "../components/SemanticTheme/StickyCheckoutNotification";
import {
  ExtraStickySettings_S as ExtraStickySettings,
  GeneralStickySettings_S as GeneralStickySettings,
  ImageInfo_S as ImageInfo,
  Maybe,
} from "@generated/types";
import { BookingJourneyStep } from "../interfaces/booking";
import { ExtrasStepperTag } from "../interfaces/extras";
import {
  getBookingStepByUrl,
  getExtrasOrUpgradesJourneyStepByTag,
} from "../utils";
import {
  RootState,
  ActiveStepperPersistState,
  removeExtraDetailsPageContent,
  ExtraListState,
  removeExtraSticky,
  removeExtraStickyWithLearnMore,
} from "@/store/actions";
import {
  bookingPersistedStateSelector,
  confirmedBookingSummarySelector,
  extraListSelector,
  extraStickySettingsSelector,
  fhBookingStepsSelector,
  fhExtrasStepsSelector,
  generalStickySettingsSelector,
  activeStepperPersistedSelector,
  stickyConfirmedCabinReservationId,
  stickyLearneMoreClicked,
  fhCabinUpgradeStepsSelector,
} from "@/store/selectors";

const DynamicFHInlineSvg = dynamic(
  () =>
    import("../components/SemanticTheme/FHInlineSvg").then(
      (module) => module.FHInlineSvg,
    ),
  { ssr: true },
);

const DynamicStickyCheckoutNotification = dynamic(
  () =>
    import("../components/SemanticTheme/StickyCheckoutNotification").then(
      (module) => module.StickyCheckoutNotification,
    ),
  { ssr: false },
);

const propKey = (props: StickyCheckoutNotificationProps) =>
  JSON.stringify({
    show: props.show,
    subtitle: props.subtitle,
    ctaButtonDisabled: props.ctaButtonDisabled,
  });

export const MemoizedSticky = memo(
  DynamicStickyCheckoutNotification,
  (prevProps, nextProps) => propKey(prevProps) === propKey(nextProps),
);

type StickyExtrasWidgetSelectorProps = {
  fhExtrasSteps: BookingJourneyStep[] | null;
  fhCabinUpgradeSteps: BookingJourneyStep[] | null;
  fhBookingSteps: BookingJourneyStep[] | null;
  generalStickySettings: GeneralStickySettings;
  extrasListState: ExtraListState;
  stickyConfirmedCabinReservationId: string;
  isLearnMoreClicked: boolean;
  bookingPersistedConfirmedCabinReservationId: string;
  extraStickySettings: ExtraStickySettings | null;
  activeStepperPersistedSelector: ActiveStepperPersistState | null;
};

export const StickyExtrasWidgetComponent: React.FC<
  StickyExtrasWidgetSelectorProps
> = ({
  fhExtrasSteps,
  fhCabinUpgradeSteps,
  fhBookingSteps,
  generalStickySettings,
  extrasListState,
  stickyConfirmedCabinReservationId,
  isLearnMoreClicked,
  extraStickySettings,
}: StickyExtrasWidgetSelectorProps): JSX.Element | null => {
  const history = useRouter();
  const dispatch = useDispatch();
  const activeBasketData = makeBasketData(extrasListState as ExtraListState);
  const [isShowing, setIsShowing] = useState(false);
  useEffect(() => {
    const currentStepPage = getBookingStepByUrl(fhExtrasSteps);
    const currentCabinUpgradesStepPage =
      getBookingStepByUrl(fhCabinUpgradeSteps);
    const currentBookingStepPage = getBookingStepByUrl(fhBookingSteps);
    const isOnExtrasJourneyPage = typeof currentStepPage !== "undefined";
    const isOnCabinUpgradesJourneyPage =
      typeof currentCabinUpgradesStepPage !== "undefined";
    const isOnBookingJourney = typeof currentBookingStepPage !== "undefined";

    if (
      isOnExtrasJourneyPage ||
      isOnCabinUpgradesJourneyPage ||
      isOnBookingJourney
    ) {
      setIsShowing(false);
      return;
    }
    if (activeBasketData && fhExtrasSteps) {
      const basketItemsCount = activeBasketData?.extras?.length;
      const showStickyOnOtherPages =
        stickyConfirmedCabinReservationId.length > 0 && basketItemsCount > 0;
      if (isLearnMoreClicked || showStickyOnOtherPages) setIsShowing(true);
    }
  }, [
    activeBasketData,
    fhExtrasSteps,
    fhCabinUpgradeSteps,
    stickyConfirmedCabinReservationId,
    history,
  ]);

  // steps in the extras journey
  const searchPage = isLearnMoreClicked
    ? getExtrasOrUpgradesJourneyStepByTag(
        ExtrasStepperTag.extras,
        fhExtrasSteps,
      )
    : getExtrasOrUpgradesJourneyStepByTag(
        ExtrasStepperTag.cabinEssentials,
        fhExtrasSteps,
      ) ||
      getExtrasOrUpgradesJourneyStepByTag(
        ExtrasStepperTag.extras,
        fhExtrasSteps,
      );

  const makeIcon = (icon?: Maybe<ImageInfo>, size?: string) =>
    icon?.url ? (
      <DynamicFHInlineSvg
        src={icon.url}
        size={!!size ? size : icon.originalHeight || "40px"}
      />
    ) : undefined;

  const makeIcons = (): StickyIcons => ({
    main: makeIcon(extraStickySettings?.extraDetailsStickyIcon),
    close: makeIcon(
      extraStickySettings?.extraDetailsStickyCloseIcon ||
        generalStickySettings?.closeIcon,
      "24px",
    ),
  });

  const abandonedBasketProps = {
    closeTitle: generalStickySettings?.abandonedBasketTitle || "",
    closeDescription: generalStickySettings?.abandonedBasketDescription || "",
    closeConfirmButton:
      generalStickySettings?.abandonedBasketConfirmLabel || "",
    closeCancelButton: generalStickySettings?.abandonedBasketCancelLabel || "",
  };

  const makeStickyLabels = (): StickyLabels => ({
    pet: "",
    pets: "",
    date: "",
    dates: "",
    guest: "",
    guests: "",
    returnButton: "",
    cta:
      extraStickySettings?.extraDetailsStickyCTAButtonText || "Back to results",
    ...abandonedBasketProps,
  });

  return (
    <MemoizedSticky
      show={isShowing}
      ctaButtonDisabled={false}
      labels={makeStickyLabels()}
      showFHModal={activeBasketData.extras.length > 0} // todo: to be confirmed by Business
      title={
        extraStickySettings?.extraDetailsStickyTitle || "Extras & Activities"
      }
      subtitle={
        activeBasketData.extras.length +
        " " +
        (extraStickySettings?.extraDetailsStickyBasketCountLabel ||
          "items in basket")
      }
      onCtaButtonClick={() => {
        dispatch(removeExtraDetailsPageContent());
        isLearnMoreClicked && dispatch(removeExtraStickyWithLearnMore());
        history.push(searchPage?.url || "");
      }}
      onCloseConfirm={() => {
        dispatch(removeExtraSticky(stickyConfirmedCabinReservationId));
        setIsShowing(false);
      }}
      icons={makeIcons()}
    />
  );
};

const mapStateToProps = (state: RootState) =>
  ({
    fhExtrasSteps: fhExtrasStepsSelector(state),
    fhCabinUpgradeSteps: fhCabinUpgradeStepsSelector(state),
    fhBookingSteps: fhBookingStepsSelector(state),
    generalStickySettings: generalStickySettingsSelector(state),
    extrasListState: extraListSelector(state),
    confirmedBookingSummaryState: confirmedBookingSummarySelector(state),
    stickyConfirmedCabinReservationId: stickyConfirmedCabinReservationId(state),
    isLearnMoreClicked: stickyLearneMoreClicked(state),
    bookingPersistedConfirmedCabinReservationId:
      bookingPersistedStateSelector(state).confirmedCabinReservationId,
    extraStickySettings: extraStickySettingsSelector(state),
    activeStepperPersistedSelector: activeStepperPersistedSelector(state),
  }) as StickyExtrasWidgetSelectorProps;

export const StickyExtrasWidget = connect(mapStateToProps)(
  StickyExtrasWidgetComponent,
);
